import { FC, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { DataList, MessageGuide, MessageModal } from 'common/components';
import { Action, ApiStatus, ButtonLabel, EntityActions } from 'common/enums';
import {
    ACTION_ITEMS,
    DELETE_POPUP_SUMMARY_PART1,
    DELETE_POPUP_SUMMARY_PART2,
    DELETE_POPUP_TITLE,
    PAGE_NUMBER_DEFAULT,
    PAGE_SIZE_DEFAULT,
    SEARCH_DEBOUNCE_DELAY
} from 'common/config';
import { PagedRequest } from 'common/models/data/pagedRequest.model';
import { RootState } from 'common/redux/core/root.reducer';
import useDebounce from 'common/hooks/useDebounce';

import ConfigurationHeader from 'modules/configuration/components/configuration-header/ConfigurationHeader';
import IndicatorSourceAddEdit from 'modules/data-management/components/indicator-source-add-edit/IndicatorSourceAddEdit';
import {
    INDICATOR_SOURCES_GUIDE,
    INDICATOR_SOURCES_TITLE,
    getIndicatorSourcesColDefs
} from 'modules/configuration/config';
import { configurationActions } from 'modules/configuration/redux';
import {
    getIndicatorSourceAddResponse,
    getIndicatorSourceEditResponse,
    getIndicatorSourcesResponse
} from 'modules/configuration/redux/configuration.selector';

import './IndicatorSources.scss';
import useIndicatorSourceAccess from 'modules/configuration/roles-access-hooks/useIndicatorSourceAccess';

const IndicatorSources: FC = () => {
    /**
     * State variable to contain search text
     */
    const [searchVal, setSearchVal] = useState<string>('');
    const [isAddEditModalVisible, setisAddEditModalVisible] = useState(false);
    /**
     * Confirmation popup details state variable
     */
    const [popupDetails, setPopupDetails] = useState({ title: '', text: '', type: '' });
    const [isShowPopup, setShowPopup] = useState<boolean>(false);
    const [modalData, setModalData] = useState<any>('');
    const [pagedRequest, setPagedRequest] = useState<PagedRequest>({
        pageNumber: PAGE_NUMBER_DEFAULT,
        pageSize: PAGE_SIZE_DEFAULT,
        searchText: ''
    });
    const { isSetUpDisabled } = useIndicatorSourceAccess();
    const indicatorSourcesResponse = useSelector((state: RootState) =>
        getIndicatorSourcesResponse(state)
    );
    const indicatorAddResponse = useSelector((state: RootState) =>
        getIndicatorSourceAddResponse(state)
    );
    const indicatorEditResponse = useSelector((state: RootState) =>
        getIndicatorSourceEditResponse(state)
    );

    const dispatch = useDispatch();

    const fetchIndicatorSources = useCallback(() => {
        dispatch(configurationActions.fetchIndicatorSources(pagedRequest));
    }, [dispatch, pagedRequest]);

    useEffect(() => {
        fetchIndicatorSources();
    }, [fetchIndicatorSources]);

    useEffect(() => {
        if (indicatorAddResponse.status === ApiStatus.SUCCESS) {
            // After add new success update page request object is default value
            setPagedRequest({
                pageNumber: PAGE_NUMBER_DEFAULT,
                pageSize: PAGE_SIZE_DEFAULT,
                searchText: ''
            });
        }

        if (
            indicatorEditResponse.status === ApiStatus.SUCCESS ||
            indicatorAddResponse.status === ApiStatus.SUCCESS
        ) {
            setisAddEditModalVisible(false);
            setModalData('');
            dispatch(configurationActions.resetIndicatorEditResponse());
            dispatch(configurationActions.resetIndicatorAddResponse());
        }
    }, [dispatch, indicatorEditResponse, indicatorAddResponse]);

    const onEdit = (data: any) => {
        setModalData(data);
        setisAddEditModalVisible(true);
    };

    const onDelete = (data: any) => {
        setPopupDetails({
            title: DELETE_POPUP_TITLE,
            text: `${DELETE_POPUP_SUMMARY_PART1} this record. ${DELETE_POPUP_SUMMARY_PART2}`,
            type: EntityActions.Delete
        });
        setModalData(data);
        setShowPopup(true);
    };

    const handleAction = (action: Action, data: any) => {
        switch (action) {
            case Action.Edit:
                onEdit(data);
                break;
            case Action.Delete:
                onDelete(data);
                break;
            default:
                break;
        }
    };

    /**
     * This function handle serach event
     * @param event Event information object created by user on search box
     */
    const onSearchChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchVal(event.target.value);
    };

    useDebounce(
        () => {
            if (searchVal.length >= 3 || searchVal.length === 0) {
                const updatedPagedRequest: PagedRequest = { ...pagedRequest };
                updatedPagedRequest.pageNumber = PAGE_NUMBER_DEFAULT;
                updatedPagedRequest.searchText = searchVal;
                setPagedRequest(updatedPagedRequest);
            }
        },
        [searchVal],
        SEARCH_DEBOUNCE_DELAY
    );

    const handlePrimaryAction = () => {
        if (popupDetails.type == EntityActions.Delete) {
            dispatch(configurationActions.deleteIndicatorSource(modalData));
        }
        setShowPopup(false);
        setModalData('');
    };

    const handleAddNewClose = () => {
        setisAddEditModalVisible(false);
        setModalData('');
    };

    const handleScroll = () => {
        if (searchVal == '' || searchVal.length < 3) {
            if (
                indicatorSourcesResponse?.status !== ApiStatus.LOADING &&
                indicatorSourcesResponse?.data?.pagination &&
                indicatorSourcesResponse?.data?.pagination.totalPages > pagedRequest.pageNumber
            ) {
                const updatedPagedRequest: PagedRequest = { ...pagedRequest };
                updatedPagedRequest.pageNumber = updatedPagedRequest.pageNumber + 1;
                setPagedRequest(updatedPagedRequest);
            }
        }
    };

    return (
        <div className="route-container">
            <ConfigurationHeader
                headerTitle={INDICATOR_SOURCES_TITLE}
                buttonTitle={ACTION_ITEMS.ADD_NEW_BUTTON}
                searchVal={searchVal}
                disableButton={isSetUpDisabled}
                isButtonVisible={true}
                hideClearButton={false}
                onSearchChangeHandler={onSearchChangeHandler}
                onAddNew={() => setisAddEditModalVisible(true)}
            />
            <MessageGuide message={INDICATOR_SOURCES_GUIDE} />
            <DataList
                isNoDataVisible={indicatorSourcesResponse?.data?.data?.length === 0}
                data={indicatorSourcesResponse?.data?.data || []}
                fetchNextDataSet={handleScroll}
                columnDefs={getIndicatorSourcesColDefs(handleAction, isSetUpDisabled)}
            />
            <MessageModal
                headerName={popupDetails.title}
                message={popupDetails.text}
                isVisible={isShowPopup}
                isSecondaryButtonVisible={true}
                primaryButton={ButtonLabel.Ok}
                secondaryButton={ButtonLabel.Cancel}
                onPrimaryAction={() => {
                    handlePrimaryAction();
                }}
                onSecondaryAction={() => {
                    setShowPopup(false);
                    setModalData('');
                }}
            />
            {isAddEditModalVisible && (
                <IndicatorSourceAddEdit
                    isVisible={isAddEditModalVisible}
                    data={modalData}
                    onClose={handleAddNewClose}
                />
            )}
        </div>
    );
};

export default IndicatorSources;
